(() => {
  const init = root => {
    if (root.epProductReady) return;
    const payload = root.querySelector('[data-ep-data]');
    if (!payload) return;
    let data;
    try { data = JSON.parse(payload.textContent); } catch { return; }
    if (!Array.isArray(data.variants) || !data.variants.length) return;

    const form = root.querySelector('[data-ep-form]');
    const variantInput = root.querySelector('[data-ep-variant]');
    if (!form || !variantInput) return;
    root.epProductReady = true;

    const variants = data.variants.map((variant, index) => ({
      ...variant, quantity_rule: variant.quantity_rule || (data.quantityRules || [])[index]
    }));
    const text = data.text || {};
    const groups = Array.from(root.querySelectorAll('[data-ep-option-index]'));
    const optionButtons = Array.from(root.querySelectorAll('[data-ep-value]'));
    const quantity = form.querySelector('[data-ep-quantity]');
    const quantityButtons = Array.from(root.querySelectorAll('[data-ep-quantity-step]'));
    const addButton = form.querySelector('[data-ep-add]') || form.querySelector('[type="submit"]');
    const addLabel = addButton && (addButton.querySelector('[data-ep-add-label]') || addButton);
    const error = root.querySelector('[data-ep-error]');
    const stage = root.querySelector('[data-ep-stage]');
    const mainImage = root.querySelector('[data-ep-main-image]');
    const views = Array.from(root.querySelectorAll('[data-ep-view]'));
    const dialog = root.querySelector('[data-ep-dialog]');
    const zoomImage = root.querySelector('[data-ep-zoom-image]');
    const colorGroup = groups.find(group => group.querySelector('.ep-swatch'));
    const colorIndex = colorGroup ? Number(colorGroup.dataset.epOptionIndex) : -1;
    const fallbackImage = (data.images || [])[0] || null;
    let selected = [];
    let current = null;
    let currentImage = null;
    let primaryImage = null;
    let dialogReturnFocus = null;
    let oldBodyOverflow;
    let formatter;
    try {
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: root.dataset.currency || 'USD'
      });
    } catch {
      formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }

    const money = cents => formatter.format(Number(cents || 0) / 100);
    const findVariant = values => variants.find(variant =>
      variant.options.length === values.length && variant.options.every((value, index) => value === values[index])
    ) || null;

    const rules = () => {
      const source = current && current.quantity_rule || {};
      const min = Math.max(1, Math.floor(Number(source.min) || 1));
      const increment = Math.max(1, Math.floor(Number(source.increment) || 1));
      const rawMax = source.max == null || source.max === '' ? Infinity : Math.floor(Number(source.max));
      const max = Number.isFinite(rawMax) ? rawMax : Infinity;
      const last = Number.isFinite(max) ? min + Math.floor((max - min) / increment) * increment : Infinity;
      return { min, max, increment, last };
    };

    const quantityValid = () => {
      if (!quantity) return true;
      const value = Number(quantity.value);
      const { min, max, increment } = rules();
      return quantity.value.trim() !== '' && Number.isSafeInteger(value) && value >= min && value <= max && (value - min) % increment === 0;
    };

    const updateQuantityButtons = () => {
      if (!quantity) return;
      const { min, last } = rules();
      const value = Number(quantity.value);
      quantityButtons.forEach(button => {
        button.disabled = !current || !current.available || last < min ||
          (Number(button.dataset.epQuantityStep) < 0 ? value <= min : value >= last);
      });
    };

    const applyQuantityRules = () => {
      if (!quantity) return;
      const { min, max, increment, last } = rules();
      quantity.min = String(min);
      quantity.step = String(increment);
      if (Number.isFinite(max)) quantity.max = String(max);
      else quantity.removeAttribute('max');
      const inputValue = Number(quantity.value);
      const rounded = Number.isFinite(inputValue) ? min + Math.round((inputValue - min) / increment) * increment : min;
      quantity.value = String(Math.max(min, Math.min(last, rounded)));
      quantity.setCustomValidity('');
      updateQuantityButtons();
    };

    const setImage = (image, element) => {
      if (!image || !image.src || !element) return;
      element.removeAttribute('srcset');
      element.removeAttribute('sizes');
      element.src = image.src;
      element.alt = image.alt || data.title || '';
      element.dataset.epImageId = String(image.id || '');
    };

    const showImage = (image, view = 'full', activeButton = null) => {
      if (!image) return;
      currentImage = image;
      setImage(image, mainImage);
      if (stage) {
        stage.classList.toggle('is-detail', view === 'detail');
        stage.classList.toggle('is-collar', view === 'collar');
        stage.dataset.epActiveView = view;
      }
      views.forEach(button => {
        const active = activeButton ? button === activeButton : button.dataset.epView === view;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });
      if (dialog && dialog.open) setImage(image, zoomImage);
    };

    const updateGallery = () => {
      const sameColor = colorIndex >= 0 ? variants.find(variant =>
        variant.options[colorIndex] === selected[colorIndex] && variant.featured_image
      ) : null;
      primaryImage = current && current.featured_image || sameColor && sameColor.featured_image || fallbackImage;
      views.forEach(button => {
        if (button.dataset.epView !== 'alternate') setImage(primaryImage, button.querySelector('img'));
      });
      showImage(primaryImage);
    };

    const setFormError = message => {
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    };

    const syncUrl = () => {
      const url = new URL(window.location.href);
      if (current) url.searchParams.set('variant', String(current.id));
      else url.searchParams.delete('variant');
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
    };

    const render = (updateUrl = true, updateImage = true) => {
      current = findVariant(selected);
      variantInput.value = current ? String(current.id) : '';
      variantInput.disabled = !current;
      const available = !!(current && current.available);
      root.dataset.epAvailable = String(available);
      const label = current ? (available ? text.add : text.soldOut) : text.unavailable;
      if (addButton) addButton.disabled = !available;
      if (addLabel && label) addLabel.textContent = label;
      root.querySelectorAll('[data-ep-price]').forEach(element => {
        element.textContent = current ? money(current.price) : text.unavailable || '';
      });
      root.querySelectorAll('[data-ep-trio-price]').forEach(element => {
        element.textContent = current ? money(current.price) : text.unavailable || '';
      });
      root.querySelectorAll('[data-ep-trio-total]').forEach(element => {
        element.textContent = current ? money(Number(current.price) * 3) : text.unavailable || '';
      });
      root.querySelectorAll('[data-ep-compare]').forEach(element => {
        const onSale = !!(current && Number(current.compare_at_price) > Number(current.price));
        element.hidden = !onSale;
        element.textContent = onSale ? money(current.compare_at_price) : '';
      });
      root.querySelectorAll('[data-ep-stock]').forEach(element => {
        element.hidden = available;
        element.textContent = (current ? text.soldOut : text.unavailable) || '';
      });
      groups.forEach(group => {
        const index = Number(group.dataset.epOptionIndex);
        group.querySelectorAll('[data-ep-value]').forEach(button => {
          const value = button.dataset.epValue;
          const active = selected[index] === value;
          const combination = selected.slice();
          combination[index] = value;
          const candidate = findVariant(combination);
          button.setAttribute('aria-pressed', String(active));
          button.disabled = false;
          button.classList.toggle('is-selected', active);
          button.classList.toggle('is-unavailable', !(candidate && candidate.available));
          button.dataset.epAvailable = String(!!(candidate && candidate.available));
        });
      });
      root.querySelectorAll('[data-ep-option-value]').forEach(element => {
        element.textContent = selected[Number(element.dataset.epOptionValue)] || '';
      });
      setFormError('');
      applyQuantityRules();
      if (updateImage) updateGallery();
      if (updateUrl) syncUrl();
      root.dispatchEvent(new CustomEvent('evilko:variant-change', { bubbles: true, detail: { variant: current } }));
    };

    optionButtons.forEach(button => button.addEventListener('click', () => {
      const group = button.closest('[data-ep-option-index]');
      if (!group) return;
      const index = Number(group.dataset.epOptionIndex);
      if (selected[index] === button.dataset.epValue) return;
      selected[index] = button.dataset.epValue;
      render();
    }));

    quantityButtons.forEach(button => button.addEventListener('click', () => {
      if (!quantity) return;
      const { min, increment, last } = rules();
      const direction = Number(button.dataset.epQuantityStep) < 0 ? -1 : 1;
      const rawValue = Number(quantity.value);
      const value = Number.isFinite(rawValue) ? rawValue : min;
      const offset = (value - min) / increment;
      const step = direction > 0 ? Math.floor(offset) + 1 : Math.ceil(offset) - 1;
      quantity.value = String(Math.max(min, Math.min(last, min + step * increment)));
      quantity.setCustomValidity('');
      setFormError('');
      updateQuantityButtons();
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }));

    if (quantity) quantity.addEventListener('input', () => {
      quantity.setCustomValidity('');
      setFormError('');
      updateQuantityButtons();
    });

    form.addEventListener('submit', event => {
      if (!current || !current.available || variantInput.value !== String(current.id)) {
        event.preventDefault();
        setFormError((current ? text.soldOut : text.unavailable) || '');
        return;
      }
      if (!quantityValid()) {
        event.preventDefault();
        const message = text.quantityError || '';
        setFormError(message);
        if (quantity) {
          quantity.setCustomValidity(message);
          quantity.reportValidity();
          quantity.focus();
        }
      }
    });

    views.forEach(button => button.addEventListener('click', () => {
      const view = button.dataset.epView;
      const image = view === 'alternate'
        ? (data.images || []).find(item => String(item.id) === String(button.dataset.epImageId))
        : primaryImage;
      if (image) showImage(image, view, button);
    }));

    const restoreDialog = () => {
      if (oldBodyOverflow !== undefined) {
        document.body.style.overflow = oldBodyOverflow;
        oldBodyOverflow = undefined;
      }
      if (dialogReturnFocus && dialogReturnFocus.isConnected) dialogReturnFocus.focus();
      dialogReturnFocus = null;
    };

    if (dialog) {
      root.querySelectorAll('[data-ep-zoom]').forEach(button => button.addEventListener('click', () => {
        if (dialog.open || !currentImage || typeof dialog.showModal !== 'function') return;
        setImage(currentImage, zoomImage);
        dialogReturnFocus = button;
        oldBodyOverflow = document.body.style.overflow;
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      }));
      dialog.querySelectorAll('[data-ep-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('close', restoreDialog);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
    }

    root.querySelectorAll('[data-ep-size-guide]').forEach(button => button.addEventListener('click', () => {
      const details = root.querySelector('[data-ep-size-details]');
      if (!details) return;
      details.open = true;
      const summary = details.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
      details.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }));

    const selectVariant = variant => {
      selected = variant.options.slice();
      render(false);
    };
    const onPopState = () => {
      const id = new URL(window.location.href).searchParams.get('variant');
      const variant = variants.find(item => String(item.id) === id) || variants.find(item => String(item.id) === String(data.selectedVariantId)) || variants[0];
      selectVariant(variant);
    };
    window.addEventListener('popstate', onPopState);
    root.epProductDestroy = () => {
      window.removeEventListener('popstate', onPopState);
      if (dialog && dialog.open) dialog.close();
      restoreDialog();
    };
    onPopState();

  };

  const boot = scope => {
    if (scope.matches && scope.matches('[data-ep-product]')) init(scope);
    scope.querySelectorAll('[data-ep-product]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = event.target.matches && event.target.matches('[data-ep-product]') ? [event.target] : event.target.querySelectorAll('[data-ep-product]');
    roots.forEach(root => { if (root.epProductDestroy) root.epProductDestroy(); });
  });
})();

